"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Switch,
  Button,
  Grid,
  styled,
  Typography,
  IconButton,
  Tooltip,
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material"
import { TimePicker } from "@mui/x-date-pickers/TimePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { ContentCopy as CopyIcon, Delete as TrashIcon } from "@mui/icons-material"
import axios from "axios"

const DayCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.2s ease-in-out",
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[2],
  },
}))

const TimePickerWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}))

const WeeklySchedule = ({ providerId }) => {
  const [schedule, setSchedule] = useState({
    monday: { enabled: false, start: null, end: null },
    tuesday: { enabled: false, start: null, end: null },
    wednesday: { enabled: false, start: null, end: null },
    thursday: { enabled: false, start: null, end: null },
    friday: { enabled: false, start: null, end: null },
    saturday: { enabled: false, start: null, end: null },
    sunday: { enabled: false, start: null, end: null },
  })
  const [loading, setLoading] = useState(true)
  const [availabilityFound, setAvailabilityFound] = useState(false)
  const [notification, setNotification] = useState(null)
  const [isAddAvailabilityDialogOpen, setIsAddAvailabilityDialogOpen] = useState(false)

  useEffect(() => {
    if (providerId) {
      onWeeklyScheduleButtonClick()
    }
  }, [providerId])

  const onWeeklyScheduleButtonClick = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/providers_test/check-availibility`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          providerId: providerId._id,
        }),
      })

      const result = await response.json()
      
      // Reset schedule first
      const initialSchedule = {
        Montag: { enabled: false, start: null, end: null },
        Dienstag: { enabled: false, start: null, end: null },
        Mittwoch: { enabled: false, start: null, end: null },
        Donnerstag: { enabled: false, start: null, end: null },
        Freitag: { enabled: false, start: null, end: null },
        Samstag: { enabled: false, start: null, end: null },
        Sonntag: { enabled: false, start: null, end: null },
      }

      // Update only the days that have availability
      result.result.forEach(item => {
        const day = item.day.charAt(0).toUpperCase() + item.day.slice(1)
        initialSchedule[day] = {
          enabled: true,
          start: new Date(`2024-01-01T${item.from}`),
          end: new Date(`2024-01-01T${item.to}`),
        }
      })

      setSchedule(initialSchedule)
      setAvailabilityFound(result.result.length > 0)
      setLoading(false)
    } catch (error) {
      console.error(error)
      setNotification({ message: "Failed to fetch schedule", severity: "error" })
      setLoading(false)
    }
  }

  const handleDayToggle = (day) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled: !prev[day].enabled,
      },
    }))
  }

  const handleTimeChange = (day, field, value) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }))
  }

  const copySchedule = (fromDay) => {
    const daySchedule = schedule[fromDay]
    setSchedule((prev) => {
      const newSchedule = { ...prev }
      Object.keys(newSchedule).forEach((day) => {
        if (day !== fromDay) {
          newSchedule[day] = { ...daySchedule }
        }
      })
      return newSchedule
    })
  }

  const clearSchedule = (day) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: { enabled: false, start: null, end: null },
    }))
  }

  const handleSubmitAvailabilityAddition = async () => {
    const apiUrl = `${process.env.REACT_APP_API_URL}/providers_test/create-availibility`

    const data = Object.keys(schedule)
      .map((day) => {
        if (schedule[day].enabled) {
          return {
            from: schedule[day].start.toTimeString().slice(0, 5),
            to: schedule[day].end.toTimeString().slice(0, 5),
            day: day.toLowerCase(),
            providerId: providerId,
          }
        }
        return null
      })
      .filter((item) => item !== null)

    if (data.length === 0) {
      setNotification({ message: "Please select at least one day and set its time range.", severity: "error" })
      return
    }

    try {
      await axios.post(apiUrl, data)
      setNotification({ message: "Availability added successfully!", severity: "success" })
      setIsAddAvailabilityDialogOpen(false)
      onWeeklyScheduleButtonClick() // Refresh the schedule
    } catch (error) {
      setNotification({ message: "Failed to add data. Please try again.", severity: "error" })
    }
  }

  const handleUpdateAvailability = async () => {
   
    const data = Object.entries(schedule).map(([day, { enabled, start, end }]) => ({
      providerId: providerId._id,
      day: day.toLowerCase(),
      from: start ? start.toTimeString().slice(0, 5) : "09:00",
      to: end ? end.toTimeString().slice(0, 5) : "17:00",
      isActive: enabled
    }));
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/providers_test/update-availability`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });
  
      const result = await response.json();
  
      if (result.status) {
        setNotification({ 
          message: result.msg || "Availability updated successfully!", 
          severity: "success" 
        });
        onWeeklyScheduleButtonClick(); // Refresh the schedule
      } else {
        setNotification({ 
          message: result.msg || "Failed to update availability.", 
          severity: "error" 
        });
      }
    } catch (error) {
      console.error("Update availability error:", error);
      setNotification({ 
        message: "Failed to update availability. Please try again.", 
        severity: "error" 
      });
    }
  };

  const renderScheduleContent = () => (
    <Grid container spacing={2}>
      {Object.entries(schedule).map(([day, { enabled, start, end }]) => (
        <Grid item xs={12} sm={6} key={day}>
          <DayCard>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch checked={enabled} onChange={() => handleDayToggle(day)} color="primary" size="small" />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: enabled ? "text.primary" : "text.disabled",
                  }}
                >
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Copy to all days">
                  <IconButton size="small" onClick={() => copySchedule(day)} sx={{ opacity: enabled ? 1 : 0.5 }}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear schedule">
                  <IconButton size="small" onClick={() => clearSchedule(day)} sx={{ opacity: enabled ? 1 : 0.5 }}>
                    <TrashIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {enabled && (
              <TimePickerWrapper>
                <TimePicker
                  label="Start Time"
                  value={start}
                  onChange={(newValue) => handleTimeChange(day, "start", newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
                <TimePicker
                  label="End Time"
                  value={end}
                  onChange={(newValue) => handleTimeChange(day, "end", newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </TimePickerWrapper>
            )}
          </DayCard>
        </Grid>
      ))}
    </Grid>
  )

  if (loading) {
    return <CircularProgress />
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {availabilityFound ? (
          <>
            {renderScheduleContent()}
            <Button variant="contained" fullWidth size="large" onClick={handleUpdateAvailability} sx={{ mt: 3 }}>
              Update Schedule
            </Button>
          </>
        ) : (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography variant="h6">No availability found</Typography>
            <Button variant="contained" onClick={() => setIsAddAvailabilityDialogOpen(true)} sx={{ mt: 2 }}>
              Add Availability
            </Button>
          </Box>
        )}

        <Dialog
          open={isAddAvailabilityDialogOpen}
          onClose={() => setIsAddAvailabilityDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add Availability</DialogTitle>
          <DialogContent>{renderScheduleContent()}</DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAddAvailabilityDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitAvailabilityAddition} variant="contained">
              Save Availability
            </Button>
          </DialogActions>
        </Dialog>

        {notification && (
          <Snackbar
            open={!!notification}
            autoHideDuration={6000}
            onClose={() => setNotification(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={() => setNotification(null)} severity={notification.severity} sx={{ width: "100%" }}>
              {notification.message}
            </Alert>
          </Snackbar>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default WeeklySchedule

